/**
 * SIG-3.13 — has anything been done on the compose screen that closing would
 * throw away?
 *
 * Its own module, and a plain function, because it is the one rule standing
 * between a mis-tapped ✕ and a form somebody spent five minutes on. Everything
 * that can close the screen asks it — the header arrow, the dialog's Escape,
 * its backdrop — so the answer has to be the same for all of them, and has to
 * be checkable without a browser.
 *
 * The screen used to ask a narrower version of this inline, over the file, the
 * title and the recipient alone. Two of the most expensive things on the screen
 * were not counted: a second signer, who is added by pressing a button and then
 * typed into, and the signature boxes placed on the pages, which are the whole
 * point of the screen and are made by tapping rather than typing.
 */
/**
 * 1.16 — one signer, as the screen now asks for them.
 *
 * The first of them is the recipient. The screen used to keep that person in
 * three fields of their own beside the document's name, which made the same
 * answer look like two different questions, and made a document with contact
 * details and no signature box look complete.
 */
export interface ComposeSigner {
  name: string;
  phone: string;
  email: string;
}

export interface ComposeWork {
  /** Files the sender picked, before they were merged into one document. */
  picked: readonly unknown[];
  /** A merge or a conversion still running — files were picked to start it. */
  preparing: boolean;
  title: string;
  /** Blocks in the digital document being built, if that is what this is. */
  blocks: number;
  /** Everybody who signs, the recipient first. An untouched row is not an answer. */
  signers: ReadonlyArray<ComposeSigner>;
  /** Signature boxes placed on the pages. */
  anchors: number;
}

/**
 * SIG-3.13 — and whether what has been done is enough to put down and come
 * back to.
 *
 * A lower bar than sending, deliberately: a send needs a channel and an address
 * on it, and somebody being interrupted has neither yet. A higher bar than
 * closing, also deliberately — what this writes is a real document with a
 * number of its own, and one with no title and no recipient is a row nobody
 * will recognise in the list a week later. The three it does ask for are
 * exactly what the server will refuse a document without.
 */
export interface ComposeKeepable {
  /** A file chosen, merged and ready — not one still being converted. */
  file: boolean;
  /** Blocks in the digital document, when the document is being typed. */
  blocks: number;
  title: string;
  /** Everybody who signs. The first of them is who the document is for. */
  signers: ReadonlyArray<ComposeSigner>;
}

const typed = (value: string): boolean => value.trim().length > 0;

/** The server's own floor for a name, so no button offers a save it refuses. */
const named = (signer: ComposeSigner | undefined): boolean => (signer?.name.trim().length ?? 0) >= 2;

export function composeCanKeep(work: ComposeKeepable): boolean {
  return (work.file || work.blocks > 0) && typed(work.title) && named(work.signers[0]);
}

export function composeHasWork(work: ComposeWork): boolean {
  return Boolean(
    work.picked.length > 0 ||
      work.preparing ||
      typed(work.title) ||
      work.blocks > 0 ||
      work.anchors > 0 ||
      work.signers.some((signer) => typed(signer.name) || typed(signer.phone) || typed(signer.email)),
  );
}

/**
 * 1.16 — and whether it can be sent.
 *
 * The rule that changed: a document is sent to the people in "who signs this
 * document", so there has to be at least one of them. Their name is what the
 * link is addressed to; the address itself is only asked of the first signer,
 * because the channel sends to them — everybody after them can be handed their
 * link by the sender.
 *
 * Extracted from the screen for the same reason as the two above: it decides
 * whether a button that sends something to a customer is pressable.
 */
export interface ComposeSendable {
  /** A file ready to upload, or a digital document that parses. */
  document: boolean;
  title: string;
  signers: ReadonlyArray<ComposeSigner>;
  channels: ReadonlyArray<'email' | 'whatsapp'>;
}

export function composeReady(work: ComposeSendable): boolean {
  const [first, ...rest] = work.signers;
  if (!named(first)) return false;

  return (
    work.document &&
    typed(work.title) &&
    rest.every(named) &&
    work.signers.every(signer => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signer.email.trim())) &&
    work.channels.length > 0 &&
    work.channels.every((channel) => typed(channel === 'email' ? first!.email : first!.phone))
  );
}
